import { Icon, Menu, MenuItem, Position } from "@blueprintjs/core"
import CustomPopover from "components/Atoms/CustomPopover"
import CustomTag from "components/Atoms/CustomTag"
import StyledLink from "components/Atoms/StyledLink"
import RowSelector, {
  RowSelectorAll,
  RowSelectorColumn,
} from "components/Molecules/RowSelector"
import OverFlowMenu from "components/Organisms/OverFlowMenu"
import CustomReactTable from "components/Templates/CustomReactTable"
import MultiLineCell from "components/Templates/table/MultiLineCell"
import TimeCell from "components/Templates/table/TimeCell"
import { useDispatchesForDate } from "graphql/dispatch/dispatch.hooks"
import { uniqBy } from "lodash-es"
import React, { useMemo } from "react"
import { TRIP_STATUSES } from "data/static.items"
import DispatchActions from "./DispatchActions"
const DispatchList = () => {
  const { data, loading } = useDispatchesForDate()

  const COLUMNS = [
    {
      ...RowSelectorColumn,
      Header({ data }) {
        return (
          <RowSelectorAll
            path="dispatches"
            payload={data?.map(({ _original: o }) => ({
              id: o?.id,
              vehicle: o?.vehicle,
            }))}
          />
        )
      },
      Cell({ original }) {
        return (
          <RowSelector
            path="dispatches"
            payload={{
              id: original?.id,
              vehicle: original?.vehicle,
            }}
          />
        )
      },
    },
    {
      width: 50,
      accessor: "bookings",
      Cell({ value: bookings }) {
        const schedules = useMemo(
          () => bookings?.map(b => b.schedule.id),
          [bookings]
        )
        return <DispatchActions schedules={schedules} />
      },
    },
    {
      Header: "Status",
      width: 130,
      accessor: "status",
      Cell({ value }) {
        const status: any = TRIP_STATUSES.find(ts => ts.id === value)
        return (
          <>
            <Icon icon="dot" intent={status.intent} /> {status.name}
          </>
        )
      },
    },
    {
      width: 180,
      Header: "Vehicle",
      accessor: "vehicle",
      Cell({ value, original }) {
        return (
          <div>
            <StyledLink to={`/d/ecom/dispatch/${original?.id}`}>
              {value?.registrationNumber}
            </StyledLink>
            <p className="vt-muted text-xs mb-0">
              {value?.vehicleType?.name} - {value?.vehicleType?.category}
            </p>
          </div>
        )
      },
    },
    {
      width: 200,
      Header: "Aggregator",
      accessor: "aggregator",
      Cell({ value }) {
        return (
          <MultiLineCell title={value?.fullName} subtitle={value?.number} />
        )
      },
    },
    {
      width: 150,
      Header: "Start Time (est.)",
      accessor: "estimatedStartTime",
      Cell({ value }) {
        return <TimeCell timeStamp={value} />
      },
    },
    {
      width: 260,
      Header: "Start And End",
      accessor: "sourceDepot",
      Cell({ value, original }) {
        return (
          <>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-5">
                {value?.name}
                <div className="vt-muted text-xs">(Source)</div>
              </div>
              <div className="col-span-2">
                <Icon className="ml-3" icon="arrow-right" />
              </div>
              <div className="col-span-5">
                {original?.destinationDepot?.name}{" "}
                <div className="vt-muted text-xs">(Destination)</div>
              </div>
            </div>
          </>
        )
      },
    },
    {
      maxWidth: 400,
      Header: "Areas Catered",
      accessor: "bookings",
      Cell({ value }) {
        const addresses = value?.map?.(({ order }) => {
          return {
            id: order?.shippingAddress?.id,
            name: order?.shippingAddress?.village,
          }
        })

        return <OverFlowMenu items={uniqBy(addresses, (a: any) => a?.id)} />
      },
    },

    {
      width: 150,
      accessor: "bookings",
      Header: "Total Quantity",
      Cell({ value, original }) {
        const items = value?.reduce((prev, i) => {
          const item = i?.orderItem
          const si = item?.sellableItem
          if (si) {
            return {
              ...prev,
              [si?.id]: {
                id: si?.id,
                name: item?.sellableItem.name,
                total:
                  typeof prev[si.id]?.total !== "undefined"
                    ? prev[si.id]?.total + i.requiredQuantity
                    : i.requiredQuantity,
              },
            }
          }
          return prev
        }, {})

        const itemsArray = items && Object.values(items)
        const total = useMemo(() => {
          return itemsArray?.reduce((p, i: any) => p + i?.total, 0)
        }, [itemsArray])
        return (
          <>
            <CustomPopover
              position={Position.BOTTOM_LEFT}
              minimal
              content={
                <Menu>
                  {itemsArray?.map((i: any) => {
                    return (
                      <MenuItem
                        key={i?.id}
                        icon="shopping-cart"
                        text={i?.name}
                        label={i?.total}
                      />
                    )
                  })}
                </Menu>
              }
            >
              <CustomTag intent="success" minimal interactive round>
                {total}
                <span className="vt-muted vt-font-sx"> Units</span>
              </CustomTag>
            </CustomPopover>
            <span>
              {original?.totalCrateCount > 0 && (
                <CustomTag intent="primary" minimal round>
                  {original?.totalCrateCount}
                  <span className="vt-muted vt-font-sx"> Crates</span>
                </CustomTag>
              )}
            </span>
          </>
        )
      },
    },
  ]
  return (
    <CustomReactTable
      loading={loading}
      loadingText="Fetching Dispatches"
      data={data?.tripsForDate ?? []}
      columns={COLUMNS}
    />
  )
}

export default DispatchList
